import { memo, useMemo, useReducer } from 'react';

import { SquarePen, Trash2 } from 'lucide-react';
import { isNullish, omit } from 'remeda';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import { CasinoAdFormDialog } from '../casinoAdFormDialog/CasinoAdFormDialog';
import { CasinoAdForm } from '../casinoAdFormDialog/form';

import css from './CasinoAdCard.module.css';

import { adsService } from 'v2/api/adsService';
import { casinoService } from 'v2/api/casinoService';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { CasinoAdModel } from 'v2/types/AdModel';
import { handleHttpError } from 'v2/utils';

interface CasinoAdCardProps {
  casinoAd: CasinoAdModel;
  onRevalidate: VoidFunction;
}

export const CasinoAdCard = memo<CasinoAdCardProps>(({ casinoAd, onRevalidate }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { data: casinos } = useSWR('getCasinos', casinoService.getCasinos);

  const { trigger: triggerUpdate, isMutating } = useSWRMutation(
    ['updateCasinoAd', casinoAd.id],
    adsService.updateCasinoAd,
    {
      onSuccess: () => {
        toast.success('The casino ad has been updated successfully');
        onRevalidate();
        setOpen();
      },
      onError: handleHttpError,
    }
  );

  const { trigger: triggerDelete } = useSWRMutation(['deleteCasinoAd', casinoAd.id], adsService.deleteCasinoAd, {
    onSuccess: () => {
      onRevalidate();
    },
    onError: handleHttpError,
  });

  const handleSubmit = (values: CasinoAdForm, resetForm: VoidFunction) => {
    triggerUpdate(values).then(() => resetForm());
  };

  const casinoName: string | undefined = useMemo(() => {
    if (isNullish(casinos)) return '';

    return casinos.find((casino) => casino.id === casinoAd.casinoId)?.name || '';
  }, [casinos, casinoAd]);

  return (
    <div className={css.casinoAdCard}>
      <div className={css.casinoAdCardHeader}>
        <p className="truncate">{casinoAd.name}</p>

        <p>Casino: {casinoName}</p>

        <div className={css.casinoAdCardTags}>
          <Chip
            label={casinoAd.isActive ? 'Active' : 'Inactive'}
            color={casinoAd.isActive ? 'success' : 'default'}
            size="small"
          />

          {casinoAd.isVideo && <Chip label="Video" color="info" size="small" />}
        </div>
      </div>

      <div className={css.casinoAdCardActions}>
        <Tooltip title="Edit" placement="top">
          <IconButton onClick={setOpen} color="warning">
            <SquarePen width={18} height={18} />
          </IconButton>
        </Tooltip>

        <Popconfirm message="Are you sure that you want to delete this casino ad?" onConfirm={triggerDelete}>
          <Tooltip title="Delete" placement="top">
            <IconButton color="error">
              <Trash2 width={18} height={18} />
            </IconButton>
          </Tooltip>
        </Popconfirm>
      </div>

      <CasinoAdFormDialog
        title="Edit Casino Ad"
        isOpen={isOpen}
        onClose={setOpen}
        initialValues={omit(casinoAd, ['id'])}
        isSubmitting={isMutating}
        onSubmit={handleSubmit}
      />
    </div>
  );
});

CasinoAdCard.displayName = 'CasinoAdCard';
